// Add this at the top of the file to extend the Window interface
import { useEffect, useState } from 'react';
import { clearAllBrowserStorage, debugLog } from 'utils/cacheUtils';

declare global {
  interface Window {
    CACHE_BUSTER_MOUNTED?: boolean;
    APP_VERSION?: any;
  }
}

interface CacheBusterProps {
  checkInterval?: number; // Not used anymore but kept for backwards compatibility
}

/**
 * Component that checks for app version changes and forces a refresh/cache clear when needed
 * This should be mounted high in the component tree (e.g., in App.tsx or layout component)
 */
export const CacheBuster = ({ checkInterval = 300000 }: CacheBusterProps): JSX.Element | null => {
  const [isOutdated, setIsOutdated] = useState(false);
  const [message, setMessage] = useState('');

  // Function to check app version and trigger refresh if needed
  const checkForUpdates = async () => {
    // Get the last refresh time
    const getCookie = (name: string): string | null => {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? match[2] : null;
    };

    const now = Date.now();
    const lastRefresh = parseInt(localStorage.getItem('lastRefresh') || getCookie('lastRefresh') || '0', 10);

    // If we refreshed in the last 10 seconds, don't check again
    if (now - lastRefresh < 10000) {
      debugLog(`Skipping update check - page was refreshed recently`, null, true);
      console.log('🔄 [Cache Buster] Skipping check - recent refresh', {
        lastRefresh: new Date(lastRefresh).toISOString(),
        now: new Date(now).toISOString(),
        elapsed: `${((now - lastRefresh) / 1000).toFixed(1)}s`
      });
      return;
    }

    try {
      // Make sure we have APP_VERSION available
      if (!window.APP_VERSION) {
        console.log('🔄 [Cache Buster] No APP_VERSION found, skipping check');
        return;
      }

      // Get values
      const latestVersion = window.APP_VERSION.version;
      const latestBuildId = window.APP_VERSION.buildId;
      const storedVersion = localStorage.getItem('currentVersion') || getCookie('currentVersion');
      const storedBuildId = localStorage.getItem('currentBuildId') || getCookie('currentBuildId');

      // Log what we're comparing
      debugLog(
        `Checking for updates`,
        {
          stored: {
            version: storedVersion,
            buildId: storedBuildId
          },
          latest: {
            version: latestVersion,
            buildId: latestBuildId
          }
        },
        true
      );

      // Skip if we already have this exact version
      if (storedVersion === latestVersion && storedBuildId === latestBuildId) {
        debugLog(`Current version matches stored version - no refresh needed`, null, true);
        return;
      }

      // Version mismatch detected
      console.log('🔄 [Cache Buster] VERSION MISMATCH DETECTED - App will refresh', {
        stored: { version: storedVersion, buildId: storedBuildId },
        latest: { version: latestVersion, buildId: latestBuildId }
      });

      // Update stored version
      localStorage.setItem('currentVersion', latestVersion);
      localStorage.setItem('currentBuildId', latestBuildId);

      // Store with cookies too
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1); // 1 year expiration
      document.cookie = `currentVersion=${latestVersion};expires=${expires.toUTCString()};path=/`;
      document.cookie = `currentBuildId=${latestBuildId};expires=${expires.toUTCString()};path=/`;

      // Set last refresh time
      localStorage.setItem('lastRefresh', now.toString());
      document.cookie = `lastRefresh=${now};expires=${expires.toUTCString()};path=/`;

      // Show notification
      setIsOutdated(true);
      setMessage('A new version is available. The page will refresh automatically in a moment...');

      // Schedule refresh after a short delay
      setTimeout(async () => {
        try {
          // Clear browser storage (except version info)
          debugLog('Starting cache clear and refresh process', null, true);
          await clearAllBrowserStorage();

          // Force reload
          debugLog('Triggering page reload', null, true);
          window.location.reload();
        } catch (error) {
          console.error('Failed to refresh application:', error);
          window.location.reload(); // Fallback
        }
      }, 2000);
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  };

  useEffect(() => {
    // Set a flag for debugging
    window.CACHE_BUSTER_MOUNTED = true;

    // Only run the version check once on initial page load,
    // with a slight delay to ensure other resources load first
    const initialCheck = setTimeout(() => {
      console.log('🔄 [Cache Buster] Component mounted and ready to check version');
      checkForUpdates();
    }, 1000);

    // Clean up
    return () => {
      window.CACHE_BUSTER_MOUNTED = false;
      debugLog('CacheBuster unmounting - cleaning up', null, true);
      clearTimeout(initialCheck);
    };
  }, []);

  // Return nothing if the app is current
  if (!isOutdated) return null;

  // Display a notification banner when the app is outdated
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: '#f8d7da',
        color: '#721c24',
        padding: '15px',
        textAlign: 'center',
        zIndex: 9999999, // Ultra high z-index to ensure visibility
        fontWeight: 'bold',
        fontSize: '16px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
        borderBottom: '2px solid #f5c6cb'
      }}>
      {message}
    </div>
  );
};
